import React, { FC, useEffect, useRef, useState } from 'react';
import { Box, Divider, LinearProgress, Typography, useTheme } from '@mui/material';
import StarRating from '../StarRating/StarRating';
import { constants } from '../../constants/constants';
import PlatformService from '../../services/PlatformService';
import { IMonitoring } from '../../shared/interfaces/IMonitoring';
import ReviewMonitoringService from '../../services/ReviewMonitoringService';
import { IGMBLocationsResponse } from '../../shared/interfaces/IGMBLocationsResponse';
import GMBService from '../../services/GMBService';
import { IPlatform } from '../../shared/interfaces/IPlatform';
import http from '../../shared/HttpCommon';
import { CanceledError } from 'axios';
import { IReviewNew } from '../../shared/interfaces/IReviewNew';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { WidgetNoData } from './WidgetNoData';

interface FilterOptions {
  commentStatus?: string;
  company?: string;
  endDate?: string;
  kind?: string;
  pageSize: number;
  offset: number;
  orderBy?: string[];
  platform?: string;
  score: number[];
  startDate?: string;
  withComments: boolean;
  withoutComments: boolean;
  isUpdated?: boolean;
  isDeleted?: boolean;
}

const defaultFilter: FilterOptions = {
  commentStatus: 'OPEN',
  pageSize: 10,
  offset: 0,
  score: [1, 5],
  withComments: true,
  withoutComments: true,
};

const WidgetReviewList: FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(true);
  const [platforms, setPlatforms] = useState<IPlatform[]>([]);
  const [monitoring, setMonitoring] = useState<IMonitoring[]>([]);
  const [reviews, setReviews] = useState<IReviewNew[]>([]);
  const [filter, setFilter] = useState<FilterOptions>(defaultFilter);
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);

  useEffect(() => {
    const controller = new AbortController();

    const getPlatforms = async () => {
      const response = await PlatformService.getPlatforms(setLoading, controller.signal);
      if (!Array.isArray(response)) return;

      setPlatforms(response);
    };
    const getMonitoring = async () => {
      let monitoringEntries: IMonitoring[] = await ReviewMonitoringService.getMonitoring(
        false,
        'reviews',
        setLoading,
        controller.signal
      );

      if (!monitoringEntries) return;

      const gmbMonitoredLocations: IGMBLocationsResponse = await GMBService.getAllLocations(
        undefined,
        1000,
        setLoading,
        controller.signal,
        true
      );

      if (!gmbMonitoredLocations) return;

      const hasGmbEntries = gmbMonitoredLocations.locations.length > 0;

      for (const location of gmbMonitoredLocations.locations) {
        const mon: IMonitoring = {
          company: location.locationID,
          createdAt: '',
          deletedAt: '',
          id: location.ID,
          jobs: [],
          platformId: -2,
          label: location.locationData.title,
          url: '',
          updatedAt: '',
          communityManagement: location.communityManagement,
          isBenchmark: false,
          type: 'LOCATION',
        };
        monitoringEntries.push(mon);
      }

      if (hasGmbEntries) {
        setPlatforms((prevState) => [...prevState, PlatformService.getPseudoGoogle()]);
      }

      setMonitoring(monitoringEntries);
      setPlatforms((prevState) => {
        return [...prevState].filter((v) => monitoringEntries.find((m) => m.platformId === v.id));
      });
    };

    (async () => {
      await getPlatforms();
      await getMonitoring();
    })();

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const getReviews = async () => {
      try {
        setLoading(true);
        const { data } = await http.post('/portal/comments/reviews', filter, {
          signal: controller.signal,
        });
        if (!data) {
          setLoading(false);
          return;
        } else {
          setLoading(false);
          const reviews = data.reviews ?? [];
          setReviews(reviews);
        }
      } catch (error) {
        if (error instanceof CanceledError) {
        } else {
          setLoading(false);
          console.error(error);
        }
      }
    };
    getReviews().catch((error) => console.log(error));

    return () => {
      controller.abort();
    };
  }, [filter]);

  useEffect(() => {
    const checkOverflow = () => {
      if (contentRef.current) {
        setIsOverflowing(contentRef.current.scrollHeight > 300);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);

    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, [reviews]);

  const sortedReviews = reviews
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, 4);

  return (
    <Box
      sx={{
        maxHeight: '290px',
        overflowY: isOverflowing ? 'auto' : 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
      ref={contentRef}
    >
      {loading ? (
        <Box>
          <LinearProgress sx={{ mt: theme.spacing(1), mb: theme.spacing(2) }} />
        </Box>
      ) : (
        <Box
          sx={{
            maxHeight: '290px',
          }}
        >
          {sortedReviews.length > 0 ? (
            sortedReviews.map((review: IReviewNew, index: number) => {
              const formattedDate = format(new Date(review.timestamp), 'dd.MM.yy', { locale: de });

              return (
                <Box
                  key={`${review.id}-${index}`}
                  sx={{ m: theme.spacing(2) }}
                >
                  <Divider sx={{ mb: theme.spacing(2) }} />
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Box sx={{ flexShrink: 0, mr: theme.spacing(1) }}>
                      <img
                        style={{ height: '40px', width: '80px', objectFit: 'contain' }}
                        alt={t('platform_logo')}
                        src={constants.platform_logos[review.platform]}
                      />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <StarRating value={review.review.rating} />
                        <Typography
                          variant="body2"
                          color="textSecondary"
                        >
                          {formattedDate}
                        </Typography>
                      </Box>
                      {review.review.header && (
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              m: theme.spacing(1),
                            }}
                          >
                            {review.review.header}
                          </Typography>
                        </Box>
                      )}
                      {review.review.reviewerTitle && (
                        <Typography
                          variant="body2"
                          sx={{
                            m: theme.spacing(1),
                          }}
                        >
                          {review.review.reviewTitle}
                        </Typography>
                      )}
                      {review.review.text && (
                        <Typography
                          variant="body2"
                          sx={{
                            m: theme.spacing(1),
                          }}
                        >
                          {review.review.text}
                        </Typography>
                      )}
                      {review.review.reviewText && (
                        <Typography
                          variant="body2"
                          sx={{
                            m: theme.spacing(1),
                          }}
                        >
                          {review.review.reviewText}
                        </Typography>
                      )}
                      {review.review.comment && (
                        <Typography
                          variant="body2"
                          sx={{
                            m: theme.spacing(1),
                          }}
                        >
                          {review.review.comment}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })
          ) : (
            <>
              <Divider sx={{ mt: theme.spacing(1), mb: theme.spacing(2) }} />
              <WidgetNoData />
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default WidgetReviewList;
